import { AlertTriangleIcon, PackageSearchIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from '@app/components/ui/button'
import { cn } from '@app/lib/cn'

/**
 * Shared small pieces for the stock screens: loading skeletons, empty and
 * error states. All of them use the design system's tokens; none of them
 * replace the shared components.
 */

export function Skeleton({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('block animate-pulse rounded-xs bg-kosele/8', className)}
    />
  )
}

export function TableSkeleton({ rows = 8, columns = 6 }: { rows?: number; columns?: number }) {
  return (
    <div className="divide-y divide-hairline">
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-3 px-3.5 py-2">
          {Array.from({ length: columns }, (_, cellIndex) => (
            <Skeleton
              key={cellIndex}
              className={cn('h-3', cellIndex === 1 ? 'flex-[2]' : 'flex-1')}
            />
          ))}
        </div>
      ))}
      <span className="sr-only">Stok verileri yükleniyor</span>
    </div>
  )
}

export interface EmptyStateProps {
  title: string
  description: string
  action?: ReactNode
}

export function EmptyState({ title, description, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
      <PackageSearchIcon className="size-5 text-ink-muted/60" aria-hidden />
      <p className="font-display text-[0.9375rem] font-semibold tracking-tight text-ink">
        {title}
      </p>
      <p className="max-w-sm text-data text-ink-muted">{description}</p>
      {action ? <div className="mt-1.5">{action}</div> : null}
    </div>
  )
}

export interface ErrorStateProps {
  title?: string
  message: string
  onRetry?: () => void
  /**
   * Replaces the retry button for errors retrying cannot fix — a 404 is the
   * server's final answer, so the stock card hands in a "back to the list"
   * link instead of a button that would fail again.
   */
  action?: ReactNode
}

export function ErrorState({ title = 'Veri alınamadı', message, onRetry, action }: ErrorStateProps) {
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-2 px-6 py-14 text-center"
    >
      <AlertTriangleIcon className="size-5 text-kordovan" aria-hidden />
      <p className="font-display text-[0.9375rem] font-semibold tracking-tight text-ink">
        {title}
      </p>
      <p className="max-w-sm text-data text-ink-muted">{message}</p>
      {action ? (
        <div className="mt-1.5">{action}</div>
      ) : onRetry ? (
        <Button variant="outline" size="sm" className="mt-1.5" onClick={onRetry}>
          Yeniden dene
        </Button>
      ) : null}
    </div>
  )
}

/** Attribute chip above the card: small label + value. */
export function AttributeChip({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex items-baseline gap-1.5 rounded-xs border border-hairline bg-surface px-1.5 py-0.5">
      <span className="text-micro uppercase tracking-[0.06em] text-ink-muted">{label}</span>
      <span className="font-mono text-data text-ink">{value}</span>
    </span>
  )
}
